import React, { useCallback, useContext, useEffect } from 'react';
import c from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import TagList from '@wix/da-ds/pkg/TagList';
import { useSubmitField } from '../../hooks/useSubmitField';
import { SubmitFormContext } from '../../context';

import s from './RecommendedTagsField.scss';

interface Props {
  withBottomMargin?: boolean;
  withBottomBorder?: boolean;
  wrapOnMobile?: boolean;
}

const RecommendedTagsField: React.FC<Props> = ({
  withBottomBorder,
  withBottomMargin,
  wrapOnMobile,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { watch, setValue } = useFormContext();
  const { initResponse } = useContext(SubmitFormContext)!;
  const { recommendedTags: initialRecommends } = initResponse || {};

  const deviationIsMature = watch('matureContent');
  const tags = watch('tags');
  const removeUsedRecommendedTags = useCallback(
    (tagList, recommends) => {
      return (recommends || [])
        .filter(({ isMature }) => (!isMature || deviationIsMature))
        .map(({ name }) => name)
        .filter(i => !(tagList || []).includes(i));
    },
    [deviationIsMature]
  );

  const {
    field: { value = [] },
  } = useSubmitField('recommendedTags', {
    initAdapter: ({ recommendedTags: initValue }) =>
      removeUsedRecommendedTags(tags, initValue),
  });

  const isEmpty = (value || []).length === 0;

  // If the user picks a recommendation, add it to tags and remove from here
  const addRecommendation = useCallback(
    e => {
      const tag = e.target.closest('[data-tagname]').dataset.tagname;
      setValue('tags', tags.concat([tag]), { shouldDirty: true });
      setValue(
        'recommendedTags',
        value.filter(i => i !== tag)
      );
    },
    [setValue, value, tags]
  );

  // If the tags change from outside (say the user adds manually),
  // or a change in original image refreshes the initResponse,
  // re-run the filter
  useEffect(() => {
    setValue(
      'recommendedTags',
      removeUsedRecommendedTags(tags, initialRecommends)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, initResponse, deviationIsMature]);

  return (
    <Section
      className={c(
        s['root'],
        withBottomBorder && s['bottom-border'],
        withBottomMargin && s['bottom-margin'],
        isEmpty && s['empty']
      )}
    >
      <Heading
        className={c(
          s['heading'],
          /**
           * We hide the heading instead of unmounting it so that the section will never be heading-less
           */
          isEmpty && s['hidden-heading']
        )}
      >
        {t('deviationSubmit.form.tags.recommended.label')}
      </Heading>
      <TagList
        size={isMobile ? 'medium' : 'small'}
        multiline={!isMobile || wrapOnMobile}
        tags={value.map(tag => ({
          tagname: tag,
          isRecommended: true,
          className: s['tag'],
          onClick: addRecommendation,
        }))}
      />
    </Section>
  );
};
RecommendedTagsField.displayName = 'RecommendedTagsField';

export default RecommendedTagsField;
